import React, { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import { mstore } from "../../store";
import { updateTitle } from "../../lib/utils";
import { secureFetch } from "../../lib/api";
import endpoint from "../../config/endpoint.json";

import { error } from "../../lib/userAlerts";
import LocationDetails from "../../components/LocationInformation/LocationDetails";
import checkAccess from '../../lib/checkAccess';
import { Loading, Button } from "@nextui-org/react";

// Constants
const FILTER_TYPES = {
  ALL: 2,
  UNVERIFIED: 1,
  FILTERED: 3,
  UNRESOLVED_COMMENTS: 4
};

const HEADINGS = {
  [FILTER_TYPES.ALL]: "All Locations",
  [FILTER_TYPES.UNVERIFIED]: "Unverified Location",
  [FILTER_TYPES.FILTERED]: "Filtered Location",
  [FILTER_TYPES.UNRESOLVED_COMMENTS]: "Location With Unresolved Comments"
};

// Component for filter section
const FilterSection = ({ 
  stopIdList, 
  stopNameList, 
  selectedStopId, 
  selectedStopName, 
  setStopId, 
  setStopName, 
  filterList, 
  getLocationForLocationInformationPage, 
  getUnresolvedCommentForLocationInformationPage 
}) => (
  <div className="flex items-center mt2">
    <div className="ml3 mr4 flex pt2 pb2 items-center">
      <Select
        name="form-field-name"
        style={{ width: "200px" }}
        placeholder="(Enter Stop Id)"
        value={selectedStopId}
        onChange={setStopId}
        options={stopIdList}
        matchPos="start"
        filterProp="value"
      />
      <div className="f5 ml3">OR</div>
      <Select
        name="form-field-name"
        style={{ width: "200px", marginLeft: 10 }}
        placeholder="(Enter Stop Name)"
        value={selectedStopName}
        onChange={setStopName}
        onInputChange={filterList}
        options={stopNameList}
      />
      <Button
        ghost
        auto
        css={{br:0, ml: 10}}
        onClick={() => getLocationForLocationInformationPage(FILTER_TYPES.FILTERED)}
      >
        Search
      </Button>
      <Button
        ghost
        auto
        css={{br:0, ml: 10}}
        onClick={() => getLocationForLocationInformationPage(FILTER_TYPES.ALL)}
      >
        All Locations
      </Button>
    </div>
    <div className="bg-gray" style={{ width: "1px", height: "30px" }}></div>
    <div className="ml4 mr4 flex pt2 pb2 items-center">
      <Button
        ghost
        auto
        css={{br:0, ml: 10}}
        color='secondary'
        onClick={getUnresolvedCommentForLocationInformationPage}
      >
        Show Location With Unsolved Comments
      </Button>
      <Button
        ghost
        auto
        css={{br:0, ml: 10}}
        color='secondary'
        onClick={() => getLocationForLocationInformationPage(FILTER_TYPES.UNVERIFIED)}
      >
        Show Unverified Locations
      </Button>
    </div>
  </div>
);

// Component for location list
const LocationList = ({ heading, columnDefs, locationList, onRowDoubleClicked, onGridReady }) => (
  <div id="scrollTop" style={{height: 'calc(100vh - 180px)'}}>
    <div className="f3 ml3 mt2">{heading}</div>
    <div
      style={{height: 'calc(100vh - 200px)'}}
      className={"ag-theme-quartz p-3"}
    >
      <AgGridReact
        columnDefs={columnDefs}
        rowData={locationList}
        pagination={true}
        paginationAutoPageSize={true}
        onRowDoubleClicked={onRowDoubleClicked}
        onGridReady={onGridReady}
        enableColResize
        enableSorting
        enableFilter
      />
    </div>
  </div>
);

const LocationInformation = () => {
  // Redux state
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );
  
  // Column definitions
  const columnDefs = useMemo(() => [
    { headerName: "Stop Id", field: "stopId" },
    { headerName: "Stop Name", field: "stopName" },
    { headerName: "Address", field: "address" },
    { headerName: "City", field: "city" },
    { headerName: "State", field: "state" },
    { headerName: "ZIP", field: "zip" },
    { headerName: "Country", field: "country" },
  ], []);

  // State
  const [state, setState] = useState({
    heading: HEADINGS[FILTER_TYPES.ALL],
    stopIdList: [],
    stopNameList: [],
    stopNameListBK: [],
    selectedStopId: "",
    selectedStopName: "",
    locationList: [],
    selectedLocation: {},
    loading: false,
    showDetails: false,
  });

  // Grid API references
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);

  // Helper function to update state
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Fetch stop IDs
  const fetchStopId = useCallback(async () => {
    try {
      const [ok, , json] = await secureFetch(endpoint.getStopIdListFromLocationTable, {});
      if (ok) {
        updateState({ stopIdList: json.stopIdList });
      } else {
        error("Oops! error occurred during fetching stop id list!");
      }
    } catch (err) {
      error("Failed to fetch stop ID list");
    }
  }, [updateState]);

  // Fetch stop names
  const fetchStopName = useCallback(async () => {
    try {
      const [ok, , json] = await secureFetch(endpoint.getStopNameListFromLocationTable, {});
      if (ok) {
        updateState({
          stopNameList: json.stopNameList,
          stopNameListBK: json.stopNameList,
        });
      } else {
        error("Oops! error occurred during fetching stop name list!");
      }
    } catch (err) {
      error("Failed to fetch stop name list");
    }
  }, [updateState]);

  // Get locations based on filter
  const getLocationForLocationInformationPage = useCallback(async (filterType) => {
    let headerName = HEADINGS[filterType];
    
    // Special case for filtered locations
    if (filterType === FILTER_TYPES.FILTERED) {
      if (!state.selectedStopId && !state.selectedStopName) {
        headerName = HEADINGS[FILTER_TYPES.ALL];
      }
    }

    updateState({
      loading: true,
      showDetails: false,
      heading: headerName,
    });

    try {
      const [ok, , json] = await secureFetch(endpoint.getLocationForLocationInformationPage, {
        stopId: state.selectedStopId,
        stopName: state.selectedStopName,
        unverifiedFilter: filterType === FILTER_TYPES.UNVERIFIED ? 1 : 0,
      });

      if (ok) {
        updateState({
          locationList: json.location,
          loading: false,
        });
      } else {
        updateState({ loading: false });
        error("Oops! error occurred during search location list!");
      }
    } catch (err) {
      updateState({ loading: false });
      error("Failed to fetch locations");
    }
  }, [state.selectedStopId, state.selectedStopName, updateState]);

  // Get locations with unresolved comments
  const getUnresolvedCommentForLocationInformationPage = useCallback(async () => {
    updateState({
      loading: true,
      showDetails: false,
      heading: HEADINGS[FILTER_TYPES.UNRESOLVED_COMMENTS],
    });

    try {
      const [ok, , json] = await secureFetch(endpoint.getUnresolvedCommentForLocationInformationPage, {
        stopId: state.selectedStopId,
        stopName: state.selectedStopName,
      });

      if (ok) {
        updateState({
          locationList: json.locationWithUnsolvedComment,
          loading: false,
        });
      } else {
        updateState({ loading: false });
        error("Oops! error occurred during fetching locations with unresolved comments!");
      }
    } catch (err) {
      updateState({ loading: false });
      error("Failed to fetch locations with unresolved comments");
    }
  }, [state.selectedStopId, state.selectedStopName, updateState]);

  // Toggle location details view
  const toggleShowDetails = useCallback((row) => {
    if (row) {
      updateState({
        showDetails: !state.showDetails,
        selectedLocation: row.data,
      });
    } else {
      getLocationForLocationInformationPage(FILTER_TYPES.ALL);
      updateState({
        showDetails: !state.showDetails,
      });
    }
  }, [state.showDetails, getLocationForLocationInformationPage, updateState]);

  // Set selected stop ID
  const setStopId = useCallback((selectedOption) => {
    updateState({ 
      selectedStopId: selectedOption ? selectedOption.value : "" 
    });
  }, [updateState]);

  // Set selected stop name
  const setStopName = useCallback((selectedOption) => {
    updateState({
      selectedStopName: selectedOption ? selectedOption.value : "",
      stopNameList: state.stopNameListBK,
    });
  }, [state.stopNameListBK, updateState]);

  // Filter stop name list
  const filterList = useCallback((input) => {
    if (input) {
      const filteredList = state.stopNameListBK.filter(name => 
        name.value.indexOf(input) !== -1
      );
      updateState({ stopNameList: filteredList });
    } else {
      updateState({ stopNameList: state.stopNameListBK });
    }
  }, [state.stopNameListBK, updateState]);

  // Delete comment from state
  const deleteCommentFromState = useCallback((value) => {
    setState(prevState => {
      const selectLocation = { ...prevState.selectedLocation };
      const comments = JSON.parse(selectLocation.comments);
      const filterCommentList = comments.filter(item => item.id !== value.id);
      selectLocation.comments = JSON.stringify(filterCommentList);
      
      return {
        ...prevState,
        selectedLocation: selectLocation
      };
    });
  }, []);

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    params.api.sizeColumnsToFit();
  }, []);

  // Initial data loading
  useEffect(() => {
    updateTitle("Location Information");
    checkAccess(clientId);
    
    fetchStopId();
    fetchStopName();
    getLocationForLocationInformationPage(FILTER_TYPES.ALL);
  }, [clientId, fetchStopId, fetchStopName, getLocationForLocationInformationPage]);

  // Render loading state
  if (state.loading) {
    return (
      <div className="flex justify-center mt6">
        <div>
          <Loading size="xl" />
          <div style={{ textAlign: "center" }}>Loading.....</div>
        </div>
      </div>
    );
  }

  // Render location details or location list
  return (
    <div>
      {state.showDetails ? (
        <LocationDetails
          getLocationWithUnsolvedComments={getLocationForLocationInformationPage}
          getUnresolvedCommentForLocationInformationPage={getUnresolvedCommentForLocationInformationPage}
          selectedLocation={state.selectedLocation}
          toggleShowDetails={toggleShowDetails}
          deleteCommentFromState={deleteCommentFromState}
        />
      ) : (
        <div className="ml3">
          <FilterSection
            stopIdList={state.stopIdList}
            stopNameList={state.stopNameList}
            selectedStopId={state.selectedStopId}
            selectedStopName={state.selectedStopName}
            setStopId={setStopId}
            setStopName={setStopName}
            filterList={filterList}
            getLocationForLocationInformationPage={getLocationForLocationInformationPage}
            getUnresolvedCommentForLocationInformationPage={getUnresolvedCommentForLocationInformationPage}
          />
          <LocationList
            heading={state.heading}
            columnDefs={columnDefs}
            locationList={state.locationList}
            onRowDoubleClicked={toggleShowDetails}
            onGridReady={onGridReady}
          />
        </div>
      )}
    </div>
  );
};

export default LocationInformation;